import { GameModel } from "@westpierstudio/luma/game";
import { reset, Resettable } from "@westpierstudio/luma/utils/reset";
import { SpinModeType, SpinSpeedType } from "@westpierstudio/core-module-oryx/components/ui/slot-game-ui/UIStateMachine";
import { ReelsModel } from "components/reels/ReelsModel";
import { WinlinesModel } from "components/winlines/WinlinesModel";
import { FREE_ROUND, OpenGameData, OpenGameEvent } from "servertypes/gameevents/OpenGame";
import { InitialSpinEvent } from "servertypes/gameevents/InitialSpin";
import { FreeSpinEvent } from "servertypes/gameevents/FreeSpin";
import { CoreSymbolID, ReelSetSymbols, WildSymbolID, Win } from "servertypes/SharedTypes";
import { getWinType, isBigWinType } from "@westpierstudio/core-module-oryx/utils/win-countup";
import { times } from "lodash";
import { devLog } from "utils/Misc";
import { Signal } from "type-signals";
import { Model } from "@westpierstudio/luma/mvc";

export enum GameState {
    INIT,
    IDLE,
    SPIN,
    SHOW_WINS,
    SHOW_BIG_WIN
}

class ModelSignals {
    gameStateChange = new Signal<(state: GameState) => void>();
}

export class PrinceOfThievesGameModel extends GameModel {
    private resetList: Resettable[] = [];
    private _openGameEvent?: OpenGameEvent;
    private _state = GameState.INIT;
    signals: ModelSignals = new ModelSignals();
    reels!: ReelsModel;
    winlines!: WinlinesModel;
    winningSymbolState: boolean[][] = [];
    spinSpeedType = SpinSpeedType.REGULAR;
    spinModeType = SpinModeType.MANUAL;
    bigWin = false;
    jackpot = false;

    get openGameData() {
        return this._openGameEvent?.body.data as OpenGameData;
    }

    get anticipation(): boolean {
        return false;
    }

    get state() {
        return this._state;
    }

    set state(state: GameState) {

        devLog(`New State: ${GameState[state]}`);
        this._state = state;
        this.signals.gameStateChange.dispatch(state);
    }

    get freeRoundId(): string | undefined {
        return this.isFreeRound ? this.activeFreeRoundData.id : undefined;
    }

    getSpinType(freeSpin: boolean) {
        return this.reels.getSpinType(freeSpin, this.spinSpeedType);
    }

    override setup(): void {
        super.setup();

        this.resetList.push(this.reels = new ReelsModel(this.services));
        this.resetList.push(this.winlines = new WinlinesModel(this.services));
    }

    processOpenGameEvent(event: OpenGameEvent): void {
        this._openGameEvent = event;
    }

    processSpinEvent(event: InitialSpinEvent | FreeSpinEvent, isFreeSpin: boolean): void {

        this.reels.spinResult = event.body.data.gameplay.reels;

        const { betData, gameplay, totalMoneyWon } = event.body.data;

        const winType = getWinType(betData.playerBet, totalMoneyWon);
        this.bigWin = isBigWinType(winType);

        this.preProcessWins(gameplay.paylineWins, gameplay.reels);
        this.jackpot = this.isJackpot(gameplay.paylineWins);

        this.setWinningSymbolState(gameplay.paylineWins);

        this.winlines.storeWins(gameplay.paylineWins);

        if (isFreeSpin && `bonusSymbolsPositions` in gameplay && gameplay.bonusSymbolsPositions) {
            this.winlines.storeFreeGameBonusWin(gameplay.bonusSymbolsPositions);
        }
    }


    protected preProcessWins(wins: Win[], reelResults: ReelSetSymbols): void {
        for (let index = 0; index < wins.length; index++) {
            const win = wins[index];
            win.winIndex = index;
            for (const position of win.pattern) {
                const symbol = reelResults[position.column][position.row];
                if (symbol === WildSymbolID.WX || symbol === WildSymbolID.WY) {
                    win.usesWild = true;
                    break;
                }
            }
        }
    }

    private isJackpot(paylineWins: Win[]): boolean {
        for (const win of paylineWins) {
            if (win.symbol == CoreSymbolID.LO && !win.usesWild && win.oakIndex == 5) {
                return true;
            }
        }
        return false;
    }

    override reset(): void {
        super.reset();
        reset(this.resetList);

        this._openGameEvent = undefined;

        this.state = GameState.INIT;
        this.winningSymbolState = [];
        this.spinSpeedType = SpinSpeedType.REGULAR;
        this.spinModeType = SpinModeType.MANUAL;
        this.bigWin = false;
    }

    private setWinningSymbolState(wins: Win[]): void {

        this.winningSymbolState = times(5, () => [false, false, false, false]);

        for (const win of wins) {
            for (const entry of win.pattern) {
                this.winningSymbolState[entry.column][entry.row] = true;
            }
        }
    }

    //------Free Rounds------
    get isFreeRound() {
        return this._freeRoundData !== undefined;
    }

    get activeFreeRoundPending() {
        return this._freeRoundData && this._freeRoundData.status === `PENDING`;
    }

    get activeFreeRoundsFinished() {
        return this._freeRoundData && this._freeRoundData.status === `FINISHED`;
    }

    get activeFreeRoundData() {
        if (!this._freeRoundData) {
            throw new Error(`Missing main model freeRoundData`);
        }
        return this._freeRoundData;
    }

    private _freeRoundData?: FREE_ROUND;

    setFreeRoundData(data: FREE_ROUND) {
        this._freeRoundData = data;
    }

    freeRoundsComplete() {
        this._freeRoundData = undefined;
    }
}